import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import AppShell from "./components/shell/AppShell";
import config from "./config";
import { useAuth } from "./contexts/AuthContext";
import { MoHProvider } from "./contexts/MoHContext";
import AddTeam from "./screens/AddTeam";
import AddUser from "./screens/AddUser";
import Apps, { useVisibleApps } from "./screens/Apps";
import ConfigureSidekick from "./screens/ConfigureSidekick";
import Dashboard from "./screens/Dashboard";
import Leaderboard from "./screens/Leaderboard";
import Login from "./screens/Login";
import Meeting from "./screens/Meeting";
import Meetings from "./screens/Meetings";
import CreateMoH from "./screens/moh/CreateMoH";
import ListMoH from "./screens/moh/ListMoH";
import GettingToKnowYou from "./screens/moh/GettingToKnowYou";
import NoAccess from "./screens/NoAccess";
import NotFound from "./screens/NotFound";
import Sidekick from "./screens/Sidekick";
import SidekickSettings from "./screens/SidekickSettings";
import TeamAdmin from "./screens/TeamAdmin";
import UpdateTeam from "./screens/UpdateTeam";
import UserAdmin from "./screens/UserAdmin";
import UserDetails from "./screens/UserDetails";

const style = {
  wrapper: {
    minHeight: "100%",
  },
};

const AppRoutes = () => {
  const { apiUser, hasFeature } = useAuth();
  const hasApps = useVisibleApps().length > 0;

  const unauthenticatedRoutes = () => {
    return (
      <>
        <Route path="/" element={<Login />} />
        <Route path="*" element={<Navigate to="/" />} />
      </>
    );
  };

  const authenticatedRoutes = () => {
    if (!apiUser?.has_sidekick && !apiUser?.has_moh) {
      return (
        <Route path="/" element={<AuthLayout />}>
          <Route index element={<NoAccess />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Route>
      );
    }

    // sidekick_configured means iPECS call recording is set up. Without it
    // Sidekick is meetings only, and admins can set calls up from Admin.
    const hasCalls = !!apiUser?.sidekick_configured;
    const canSeeLeaderboard =
      hasCalls && (apiUser?.is_admin || hasFeature(["sidekick_supervisor"]));
    const hasDashboard = hasCalls && config.dashboard_enabled;

    return (
      <Route path="/" element={<AuthLayout />}>
        {apiUser?.has_sidekick && (
          <>
            <Route
              index
              element={
                <Navigate to={hasCalls ? "/calls" : "/meetings"} replace />
              }
            />
            {/* /calls/:sqid opens a call in place of the lists */}
            {hasCalls && <Route path="/calls/*" element={<Sidekick />} />}
            <Route path="/meetings" element={<Meetings />} />
            <Route path="/meetings/:sqid" element={<Meeting />} />
            {hasApps && <Route path="/apps" element={<Apps />} />}
            {canSeeLeaderboard && (
              <>
                <Route path="/leaderboard" element={<Leaderboard />} />
                {/* Old links */}
                {[
                  "/admin/leaderboard",
                  "/insights",
                  "/insights/leaderboard",
                ].map((path) => (
                  <Route
                    key={path}
                    path={path}
                    element={<Navigate to="/leaderboard" replace />}
                  />
                ))}
              </>
            )}
            {hasDashboard && (
              <>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route
                  path="/insights/dashboard"
                  element={<Navigate to="/dashboard" replace />}
                />
                {!canSeeLeaderboard && (
                  <Route
                    path="/insights"
                    element={<Navigate to="/dashboard" replace />}
                  />
                )}
              </>
            )}
            {apiUser?.is_admin && (
              <>
                <Route path="/admin/teams/create" element={<AddTeam />} />
                <Route path="/admin/teams/:teamId" element={<UpdateTeam />} />
                <Route path="/admin/teams" element={<TeamAdmin />} />
                <Route path="/admin/users/create" element={<AddUser />} />
                <Route path="/admin/users/:userId" element={<UserDetails />} />
                <Route path="/admin/users" element={<UserAdmin />} />
                <Route
                  path="/admin/sidekick-settings"
                  element={<SidekickSettings />}
                />
                {!hasCalls && (
                  <Route
                    path="/admin/call-recording"
                    element={<ConfigureSidekick />}
                  />
                )}
                <Route path="/admin" element={<Navigate to="/admin/users" />} />
              </>
            )}
          </>
        )}

        {apiUser?.has_moh && (
          <Route
            element={
              <MoHProvider>
                <Outlet />
              </MoHProvider>
            }
          >
            {!apiUser?.has_sidekick && (
              <Route index element={<Navigate to="/admin/moh" />} />
            )}
            <Route path="/admin/moh" element={<ListMoH />} />
            <Route path="/admin/moh/create" element={<CreateMoH />} />
            <Route path="/admin/moh/create/:id" element={<CreateMoH />} />
            <Route
              path="/admin/moh/getting-to-know-you"
              element={<GettingToKnowYou />}
            />
          </Route>
        )}

        <Route path="*" element={<NotFound />} />
      </Route>
    );
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Wrapper />}>
          {apiUser ? authenticatedRoutes() : unauthenticatedRoutes()}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

function Wrapper() {
  return (
    <div style={style.wrapper}>
      <Outlet />
    </div>
  );
}

function AuthLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

export default AppRoutes;
